import React, {
  useState, useEffect, useCallback, useRef,
} from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { push } from 'connected-react-router';
import { OTHER_DISEASE } from 'utils/constants/disease';
import { usePathNameChanged } from 'utils/hooks/usePathNameChanged';
import { useAuth } from 'utils/hooks/useAuth';
import { getUserCondition } from 'modules/user/utils';
import { selectIsAnyPopupShown } from 'modules/popup/popup.selectors';
import app from 'new-ui/app';
import { ROUTE_NAME, ROUTE_PATH } from 'utils/constants/router';
import { usePageHeader } from './PageHeaderContext';
import { PageHeaderView } from './PageHeaderView';
import { SIZES, TAB_IDS, VERSIONS } from './constants';

// TODO move click handler logic to page components
const getLogoClickHandler = ({
  dispatch, isAuthenticated, customerRef, customLogoClickHandler,
}) => () => {
  if (customLogoClickHandler?.() === false) {
    return;
  }
  if (
    app.isCurrentPage(ROUTE_NAME.RESULTS) || app.isCurrentPage(ROUTE_NAME.FINANCE)
    || app.isCurrentPage(ROUTE_NAME.CHAT)
  ) {
    dispatch(push(ROUTE_PATH[ROUTE_NAME.FEED]));
    return;
  }
  if (isAuthenticated) {
    dispatch(push(ROUTE_PATH[customerRef ? ROUTE_NAME.FEED : ROUTE_NAME.RESULTS]));
  } else {
    dispatch(push(ROUTE_PATH[ROUTE_NAME.HOME]));
  }
};

export const PageHeader = () => {
  const dispatch = useDispatch();
  const mobileMenuClosedActionRef = useRef(null);
  const { isAuthenticated, isAuthPending } = useAuth();
  const isAnyPopupShown = useSelector((state) => selectIsAnyPopupShown(state));
  const { data: profile } = useSelector((state) => state.profile);
  const { providerSpecific: { code: providerCode } = {} } = useSelector((state) => state.appConfiguration);

  const { personal: { customerRef } = {} } = profile || {};
  const condition = getUserCondition(profile);

  const {
    onLogoClick,
    headerContent, // complete replacement of the header tag content
    headerSettings,
    customLogoScr, // getProvidersAssetUrl(appConfiguration.providerSpecific?.logoFileName)
    headerRightSlot,
  } = usePageHeader();

  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    if (!isAnyPopupShown || !isMobileMenuOpen) {
      return;
    }
    setIsMobileMenuOpen(false);
  }, [isAnyPopupShown, isMobileMenuOpen]);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 1024 && isMobileMenuOpen) {
        setIsMobileMenuOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isMobileMenuOpen]);

  usePathNameChanged(useCallback(() => {
    setIsMobileMenuOpen(false);
  }, []));

  useEffect(() => {
    if (isMobileMenuOpen || !mobileMenuClosedActionRef.current) {
      return;
    }
    mobileMenuClosedActionRef.current(dispatch);
    mobileMenuClosedActionRef.current = null;
  }, [dispatch, isMobileMenuOpen]);

  const {
    isLogoShown,
    isProfileButtonShown,
    isUploadButtonShown,
    isNavMenuShown,
    version,
    isLanguageMenuShown,
    isLoginButtonShown,
    isBurgerMenuShown,
    tabIds,
  } = headerSettings;

  const finalTabIds = tabIds.filter((i) => (
    i !== TAB_IDS.TREATMENT_OPTIONS
    || (i === TAB_IDS.TREATMENT_OPTIONS && !customerRef && condition !== OTHER_DISEASE)
  ));

  const handleMobileMenuItemClick = (action) => {
    if (isMobileMenuOpen) {
      mobileMenuClosedActionRef.current = action;
      setIsMobileMenuOpen(false);
    } else {
      action(dispatch);
    }
  };

  return (
    <PageHeaderView
      isAuthenticated={isAuthenticated}
      isMobileMenuOpen={isMobileMenuOpen}
      isLogoShown={!providerCode && isLogoShown}
      isNavMenuShown={isNavMenuShown}
      isBurgerMenuShown={isAuthenticated && isBurgerMenuShown}
      isProfileButtonShown={isProfileButtonShown}
      isUploadButtonShown={isUploadButtonShown}
      isLoginButtonShown={!isAuthenticated && !isAuthPending && isLoginButtonShown}
      isLanguageMenuShown={isLanguageMenuShown}
      tabIds={finalTabIds}
      onLogoClick={getLogoClickHandler({
        isAuthenticated, customerRef, dispatch, customLogoClickHandler: onLogoClick,
      })}
      onHelpWithTinaClick={() => {
        dispatch(push(ROUTE_PATH[ROUTE_NAME.CHAT]));
      }}
      toggleMobileMenu={() => setIsMobileMenuOpen((v) => !v)}
      onMobileMenuItemClick={handleMobileMenuItemClick}
      headerContent={headerContent}
      headerRightSlot={headerRightSlot}
      customLogoScr={customLogoScr}
      size={providerCode && version !== VERSIONS.HCP ? SIZES.SMALL : SIZES.DEFAULT}
      version={version}
    />
  );
};
